import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useTranslation from "@/hooks/useTranslation";
import GeoFilterBar from "@/components/GeoFilterBar";
import TopNav from "@/components/TopNav";
import {
  ArrowLeft, Building2, Search, MapPin, Globe, Tag,
  Loader2, Landmark, Heart, Briefcase, Verified, ExternalLink,
} from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const SUBCATEGORIES = {
  government: ["National", "Provincial", "Municipal", "State-Owned Entity", "Regulatory"],
  ngos: ["Human Rights", "Education", "Health", "Environment", "Community", "Relief"],
  businesses: ["Technology", "Finance", "Manufacturing", "Retail", "Healthcare", "Education", "Energy", "Agriculture", "Logistics", "Tourism"],
};

const dummyLogo = (name) => {
  const colors = ["from-violet-500 to-purple-600", "from-blue-500 to-cyan-600", "from-emerald-500 to-teal-600", "from-amber-500 to-orange-600", "from-rose-500 to-pink-600"];
  return { bg: colors[(name?.charCodeAt(0) || 0) % colors.length], letter: (name || "O")[0].toUpperCase() };
};

export default function CorporateHub() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeSubHub, setActiveSubHub] = useState("all");
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const [radius, setRadius] = useState("50");
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterRegion, setFilterRegion] = useState("all");
  const [radiusKm, setRadiusKm] = useState("");
  const [corporates, setCorporates] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCorporates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/v2/orgs`);
      setCorporates(res.data.items || []);
    } catch { setCorporates([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCorporates(); }, [fetchCorporates]);

  const govCount = corporates.filter(c => c.org_type === "government").length;
  const ngoCount = corporates.filter(c => c.org_type === "ngo").length;
  const bizCount = corporates.filter(c => c.org_type === "business").length;

  const subHubs = [
    { id: "all", label: "All", icon: Building2, color: "bg-violet-600", count: String(corporates.length) },
    { id: "government", label: t("corporate.government"), icon: Landmark, color: "bg-blue-500", count: String(govCount) },
    { id: "ngos", label: t("corporate.ngos"), icon: Heart, color: "bg-emerald-500", count: String(ngoCount) },
    { id: "businesses", label: t("corporate.businesses"), icon: Briefcase, color: "bg-violet-500", count: String(bizCount) },
  ];

  const filtered = corporates.filter(c => {
    if (activeSubHub !== "all" && c.org_type !== activeSubHub) return false;
    const q = search.toLowerCase();
    if (search && !(c.name || "").toLowerCase().includes(q) && !(c.subdomain || "").includes(q) && !(c.description || "").toLowerCase().includes(q)) return false;
    if (category && category !== "all" && c.category !== category) return false;
    if (location && !(c.country || "").toLowerCase().includes(location.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <header className="border-b border-violet-800/30 bg-violet-950/60 backdrop-blur-xl sticky top-0 z-50 px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white p-1"><ArrowLeft className="w-5 h-5" /></button>
        <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
        <span className="font-bold text-lg">{t("corporate.title")}</span>
        <span className="text-xs text-zinc-500">{corporates.length} organizations</span>
      </header>
      <div className="border-b border-violet-800/20 bg-violet-950/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-2 py-3 overflow-x-auto">
            {subHubs.map(sh => (
              <button key={sh.id} onClick={() => setActiveSubHub(sh.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap ${activeSubHub === sh.id ? `${sh.color} text-white shadow-lg` : "bg-violet-900/30 text-violet-200 hover:bg-violet-800/40 hover:text-white"}`}>
                <sh.icon className="w-4 h-4" />
                {sh.label}
                <span className="ml-1 text-[10px] opacity-60">{sh.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <GeoFilterBar country={filterCountry} onCountryChange={setFilterCountry} region={filterRegion} onRegionChange={setFilterRegion} radius={radiusKm} onRadiusChange={setRadiusKm} />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input placeholder="Search by name, subdomain..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-violet-950/40 border-violet-800/40 text-white rounded-full text-sm h-9 placeholder:text-violet-300/60" />
          </div>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input placeholder="Location (country)" value={location} onChange={e => setLocation(e.target.value)}
              className="pl-9 bg-violet-950/40 border-violet-800/40 text-white rounded-full text-sm h-9 placeholder:text-violet-300/60" />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="bg-violet-950/40 border-violet-800/40 text-white rounded-full text-sm h-9">
              <Tag className="w-4 h-4 mr-2 text-zinc-500" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-violet-950 border-violet-700/40 text-white">
              <SelectItem value="all">All Categories</SelectItem>
              {SUBCATEGORIES[activeSubHub === "all" ? "businesses" : activeSubHub]?.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>) || []}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-zinc-500 shrink-0" />
            <Input type="number" placeholder="Radius" value={radius} onChange={e => setRadius(e.target.value)}
              className="bg-violet-950/40 border-violet-800/40 text-white rounded-full text-sm h-9 text-center placeholder:text-violet-300/60" min={1} max={500} />
            <span className="text-xs text-zinc-500">km</span>
          </div>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-6 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-zinc-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg">No organizations found</p>
            <p className="text-sm text-zinc-600">Try adjusting your search filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c, i) => {
              const logo = dummyLogo(c.name);
              return (
                <Card key={c.id || i}
                  className="bg-app-card border-white/10 p-0 hover:bg-violet-900/30 transition-all hover:border-violet-400/50 overflow-hidden group cursor-pointer"
                  onClick={() => navigate(`/corporate-hub/${c.id}`)}>
                  <div className="p-4 pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${logo.bg} flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-lg`}>
                          {logo.letter}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-semibold text-sm truncate">{c.name}</p>
                            {c.verified && <Verified className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                          </div>
                          {c.subdomain && <p className="text-[11px] text-zinc-500">{c.subdomain}.allbaise.com</p>}
                        </div>
                      </div>
                    </div>
                    {c.description && <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{c.description}</p>}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {c.services && Array.isArray(c.services) && c.services.slice(0, 2).map((s, i) => (
                        <Badge key={i} className="bg-amber-600/10 text-amber-300 border-amber-600/20 text-[8px]">{s}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="px-4 pb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-violet-600/10 text-violet-300 border-violet-600/20 text-[10px]">{c.org_type}</Badge>
                      {c.country && <Badge className="bg-zinc-600/10 text-zinc-400 border-zinc-600/20 text-[10px]">{c.country}</Badge>}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); navigate(`/corporate-hub/${c.id}`); }}
                      className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 opacity-0 group-hover:opacity-100 transition-opacity">
                      Click for more <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
